/**
 * ユーザーの「忙しさ」(`capacity_index`) に関する共通定数・ヘルパー。
 *
 * - `CAPACITY_LEVELS` / `getCapacityLabel`: `/api/me` で扱う標準 5 段階の表示名。
 * - `isCapacityFresh`: 直近 3 日以内に更新された値かどうかを判定する。
 *   3 日でリセットされる仕様 (`/api/me` の `capacity_index_updated_at`) に従い、
 *   オファー作成のユーザー選択詳細では古い値を表示しない判断に使う。
 *
 * ラベル文言はマイページ「今の忙しさ」(`OfferConditions.jsx`) の UI と同期させる。
 */

/** `capacity_index` の標準値（5 段階）。任意テキストはサーバ側でも許容される。 */
export const CAPACITY_LEVELS = [
  { value: "one", label: "いま、のんびり中" },
  { value: "two", label: "そこそこ余裕あり" },
  { value: "three", label: "ちょっと込み合い中" },
  { value: "four", label: "かなり集中モード" },
  { value: "five", label: "もう限界間近" },
] as const;

export type CapacityLevelValue = (typeof CAPACITY_LEVELS)[number]["value"];

/** 「3 日でリセット」の閾値。`isCapacityFresh` のしきい値。 */
export const CAPACITY_FRESHNESS_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * `capacity_index` の生値から日本語ラベルを取り出す。
 *
 * - 標準値 `one`〜`five` に該当する場合は対応するラベル。
 * - 標準値以外の任意テキストはそのまま返す（サーバ側で最大 40 文字制限）。
 * - `null` / `undefined` / 空文字は `null`。
 */
export const getCapacityLabel = (
  value: string | null | undefined,
): string | null => {
  if (!value) return null;
  const matched = CAPACITY_LEVELS.find((l) => l.value === value);
  return matched ? matched.label : value;
};

/**
 * `capacity_index_updated_at` の値が「3 日以内に更新された値」かを判定する。
 *
 * - `null` / 不正な日付は常に `false`。
 * - `now` を渡せばテスト時に時刻を固定できる（既定は呼び出し時の `Date`）。
 */
export const isCapacityFresh = (
  updatedAt: string | null | undefined,
  now: Date = new Date(),
): boolean => {
  if (!updatedAt) return false;
  const ts = new Date(updatedAt).getTime();
  if (Number.isNaN(ts)) return false;
  return now.getTime() - ts < CAPACITY_FRESHNESS_MS;
};
